import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { inputStyle } from './Modal';

/**
 * <Combobox /> — a type-to-search picker.
 *
 * Replaces the native <select> used for pickers whose list is long enough that
 * scrolling isn't realistic (properties, clients, job titles, work types). An FM
 * with hundreds of buildings can't hunt a dropdown — they expect to type a few
 * letters and see it. A native select swallows typing, which reads as "the search
 * box doesn't accept input".
 *
 * Props:
 *   value        currently selected value ('' = nothing)
 *   onChange     (value) => void
 *   options      string[] | { value, label, hint?, group? }[]
 *   placeholder  shown when nothing is selected
 *   onAddNew     (text) => void — enables an inline "＋ Add …" row for new values
 *   allowClear   show the ✕ (default true)
 *   disabled
 *   emptyText    shown when the filter matches nothing
 */

const toOpt = (o) => (typeof o === 'string' ? { value: o, label: o } : ({ ...o, label: o.label ?? String(o.value ?? '') }));
const fold = (s) => String(s || '').toLowerCase();
let uid = 0;

const Combobox = ({
  value, onChange, options = [], placeholder = 'Select…', onAddNew,
  allowClear = true, disabled = false, emptyText = 'No matches',
  id, 'aria-describedby': describedBy, 'aria-invalid': ariaInvalid, 'aria-required': ariaRequired, 'aria-label': ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [dropUp, setDropUp] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listId = useMemo(() => { uid += 1; return `cw-combo-${uid}`; }, []);

  const opts = useMemo(() => options.map(toOpt).filter((o) => o.value !== undefined && o.value !== null), [options]);
  const selected = useMemo(() => opts.find((o) => String(o.value) === String(value)), [opts, value]);
  // A value that isn't in the list is still a real value (a custom / free-text one).
  const selectedLabel = selected ? selected.label : (value ? String(value) : '');

  const filtered = useMemo(() => {
    const q = fold(query).trim();
    if (!q) return opts;
    return opts.filter((o) => fold(o.label).includes(q) || fold(o.hint).includes(q) || fold(o.value).includes(q));
  }, [opts, query]);

  const trimmed = query.trim();
  const canAdd = !!onAddNew && !!trimmed && !opts.some((o) => fold(o.label) === fold(trimmed));
  const rowCount = filtered.length + (canAdd ? 1 : 0);

  const close = useCallback(() => { setOpen(false); setQuery(''); setActive(0); }, []);

  const LIST_MAX = 264;
  const openList = () => {
    if (disabled || open) return;
    // Drop upward when the list wouldn't fit below (bottom of a filter panel, a
    // field low in a modal), so options are never off-screen.
    const r = inputRef.current?.getBoundingClientRect();
    if (r) setDropUp(window.innerHeight - r.bottom < LIST_MAX + 16 && r.top > LIST_MAX + 16);
    setOpen(true);
    setQuery('');
    // Start on the current selection so Enter re-picks it rather than jumping.
    const i = filtered.findIndex((o) => String(o.value) === String(value));
    setActive(i >= 0 ? i : 0);
  };

  const pick = useCallback((opt) => { onChange(opt.value); close(); }, [onChange, close]);
  const addNew = useCallback(() => {
    if (!canAdd) return;
    onAddNew(trimmed);
    onChange(trimmed);
    close();
  }, [canAdd, onAddNew, onChange, trimmed, close]);

  // Click-away closes without committing.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) close(); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  // Keep the highlighted row in view when arrowing through a long list.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector('[data-active="1"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || (e.key === 'Tab' && false)) {
      e.preventDefault();
      if (!open) { openList(); return; }
      setActive((i) => (rowCount ? (i + 1) % rowCount : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { openList(); return; }
      setActive((i) => (rowCount ? (i - 1 + rowCount) % rowCount : 0));
    } else if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault();
      if (canAdd && active === filtered.length) addNew();
      else if (filtered[active]) pick(filtered[active]);
    } else if (e.key === 'Escape') {
      if (open) { e.preventDefault(); e.stopPropagation(); close(); }
    } else if (e.key === 'Backspace' && !query && value && open) {
      onChange('');
    }
  };

  // Group headers, preserving the order groups first appear in.
  const grouped = useMemo(() => {
    if (!filtered.some((o) => o.group)) return [{ group: null, items: filtered }];
    const order = [];
    const map = new Map();
    filtered.forEach((o) => {
      const g = o.group || '';
      if (!map.has(g)) { map.set(g, []); order.push(g); }
      map.get(g).push(o);
    });
    return order.map((g) => ({ group: g || null, items: map.get(g) }));
  }, [filtered]);

  let flat = -1;   // running index across groups, to match `active`

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        id={id}
        role="combobox"
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        disabled={disabled}
        className="cw-combo-input"
        style={{ ...inputStyle, paddingRight: allowClear && value ? 52 : 30, cursor: disabled ? 'not-allowed' : 'text' }}
        value={open ? query : selectedLabel}
        placeholder={open && selectedLabel ? selectedLabel : placeholder}
        onChange={(e) => { setQuery(e.target.value); setActive(0); if (!open) setOpen(true); }}
        onFocus={openList}
        onMouseDown={openList}
        onKeyDown={onKeyDown}
      />
      <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: 2, pointerEvents: 'none' }}>
        {allowClear && !!value && (
          <button type="button" className="cw-combo-x" title="Clear" style={{ pointerEvents: 'auto' }}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={(e) => { e.stopPropagation(); onChange(''); setQuery(''); inputRef.current?.focus(); }}>
            <i className="fa-solid fa-xmark" />
          </button>
        )}
        <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: '0.7rem', color: '#9ca3af' }} />
      </div>

      {open && (
        <div ref={listRef} id={listId} role="listbox" className={`cw-combo-list${dropUp ? ' up' : ''}`}>
          {grouped.map(({ group, items }) => (
            <React.Fragment key={group || '_'}>
              {group && <div className="cw-combo-group">{group}</div>}
              {items.map((o) => {
                flat += 1;
                const i = flat;
                const isSel = String(o.value) === String(value);
                return (
                  <div
                    key={`${o.value}`}
                    role="option"
                    aria-selected={isSel}
                    data-active={i === active ? '1' : '0'}
                    className={`cw-combo-opt${i === active ? ' active' : ''}${isSel ? ' selected' : ''}`}
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => e.preventDefault()}   // keep focus so blur doesn't beat the click
                    onClick={() => pick(o)}
                  >
                    <span className="l">{o.label}</span>
                    {o.hint && <span className="h">{o.hint}</span>}
                    {isSel && <i className="fa-solid fa-check tick" />}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
          {canAdd && (() => {
            flat += 1;
            const i = flat;
            return (
              <div role="option" aria-selected={false} data-active={i === active ? '1' : '0'}
                className={`cw-combo-opt add${i === active ? ' active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={addNew}>
                <i className="fa-solid fa-plus" /> Add “{trimmed}”
              </div>
            );
          })()}
          {!filtered.length && !canAdd && <div className="cw-combo-empty">{emptyText}</div>}
        </div>
      )}
    </div>
  );
};

export default Combobox;
